import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { Eye, EyeSlash, CheckCircleFill, Circle } from 'react-bootstrap-icons';
import './PasswordField.css';

interface PasswordRequirement {
  text: string;
  regex?: RegExp;
  minLength?: number;
}

interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  requirements?: PasswordRequirement[];
  showStrengthIndicator?: boolean;
  strengthLengthBonus?: number;
  name?: string;
}

const defaultRequirements: PasswordRequirement[] = [
  { text: 'At least 8 characters', minLength: 8 },
  { text: 'At least 1 capital letter', regex: /[A-Z]/ },
  { text: 'At least 1 number', regex: /\d/ },
  { text: 'At least 1 special character', regex: /[^A-Za-z\d\s]/ }
];

const PasswordField: React.FC<PasswordFieldProps> = ({
  label,
  value,
  onChange,
  placeholder = 'Enter your password',
  error,
  disabled = false,
  requirements = defaultRequirements,
  showStrengthIndicator = true,
  strengthLengthBonus = 12,
  name = 'password'
}) => {
  const [showPassword, setShowPassword] = useState(false);

  const checkRequirement = (req: PasswordRequirement): boolean => {
    if (req.minLength) {
      return value.length >= req.minLength;
    }
    if (req.regex) {
      return req.regex.test(value);
    }
    return false;
  };

  const getPasswordRequirements = () => {
    return requirements.map(req => ({
      text: req.text,
      met: checkRequirement(req)
    }));
  };

  const getPasswordStrength = () => {
    if (!value) return 0;
    const requirementsMet = getPasswordRequirements();
    let metCount = requirementsMet.filter(r => r.met).length;

    if (metCount === requirements.length && value.length > strengthLengthBonus) {
      metCount = 5;
    }

    return metCount;
  };

  const getPasswordStrengthColor = (segmentIndex: number, strength: number) => {
    if (segmentIndex >= strength) return '#e0e0e0';

    if (strength <= 1) return '#dc3545';
    if (strength === 2) return '#fd7e14';
    if (strength === 3) return '#ffc107';
    if (strength === 4) return '#198754';
    return '#198754';
  };

  const getPasswordStrengthLabel = (strength: number) => {
    if (strength === 0) {
      return null; // Don't show label if no indicators filled
    }
    if (strength <= 2) {
      return { text: 'WEAK', color: '#dc3545' }; // Red for weak (1-2 bars)
    }
    if (strength === 3) {
      return { text: 'MEDIUM', color: '#ffc107' }; // Yellow for medium (3 bars)
    }
    if (strength >= 4) {
      return { text: 'STRONG', color: '#198754' }; // Green for strong (4-5 bars)
    }
    return null;
  };

  return (
    <Form.Group className="mb-3">
      <Form.Label>{label} <span className="required-label">*</span></Form.Label>
      <div className="password-field-wrapper">
        <Form.Control
          type={showPassword ? 'text' : 'password'}
          name={name}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          isInvalid={!!error}
          className="password-input-padded"
        />
        <Button
          variant="link"
          onClick={() => setShowPassword(!showPassword)}
          disabled={disabled}
          type="button"
          className="password-visibility-toggle"
        >
          {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
        </Button>
      </div>

      {showStrengthIndicator && (
        <>
          <div className="mt-2 d-flex gap-1 mb-2">
            {[0, 1, 2, 3, 4].map((index) => (
              <div
                key={index}
                className="password-strength-segment"
                style={{
                  backgroundColor: getPasswordStrengthColor(index, getPasswordStrength())
                }}
              />
            ))}
          </div>
          {getPasswordStrengthLabel(getPasswordStrength()) && (
            <div className="password-strength-label-container">
              <span
                className="password-strength-label"
                style={{
                  color: getPasswordStrengthLabel(getPasswordStrength())?.color
                }}
              >
                {getPasswordStrengthLabel(getPasswordStrength())?.text}
              </span>
            </div>
          )}
        </>
      )}

      {error && <Form.Text className="text-danger">{error}</Form.Text>}

      {value && !error && (
        <div className="mt-2">
          {getPasswordRequirements().map((req, index) => (
            <div key={index} className="d-flex align-items-center mb-1 password-requirement-item">
              {req.met ? (
                <CheckCircleFill size={16} className="text-success me-2" />
              ) : (
                <Circle size={16} className="me-2 password-requirement-icon-unmet" />
              )}
              <span className={req.met ? 'text-success' : 'text-muted'}>
                {req.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </Form.Group>
  );
};

export default PasswordField;